import { AUDIENCES, type DocMeta } from "@klarity/schema";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { FileText, LayoutTemplate, NotebookText } from "lucide-react";
import { type FormEvent, type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Field, TextInput } from "../../components/ui/Field";
import { DocMarks, PageTitle, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { Select } from "../../components/ui/Select";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { DocKind, Template } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { cleanMeta, type MetaErrors, validateMeta } from "../../lib/meta";
import { useSpaces, useTemplates, useThemes } from "../../lib/queries";
import { MetaForm } from "../document/MetaForm";

type Choice = { kind: DocKind; template: Template | null };

const prefixOf = (code: string | null) => (code?.includes("-") ? `${code.split("-")[0]}-` : "");
const SPACE_GROUPS = [
  ["client", "Clients"],
  ["internal", "Handbook"],
  ["templates", "Templates"],
] as const;
const startMeta = (t: Template | null): DocMeta => {
  if (!t) return {};
  const { code: _code, ...rest } = t.meta;
  return { ...rest, code: prefixOf(t.code) };
};

export function NewDocumentPage(props: { space?: string; parent?: string; template?: string; kind?: string }) {
  const me = useMe();
  const navigate = useNavigate();
  const { data: spaces = [] } = useSpaces();
  const { data: templates, isLoading } = useTemplates();
  const { data: themes = [] } = useThemes();

  const [choice, setChoice] = useState<Choice | null>(null);
  const [spaceId, setSpaceId] = useState(props.space ?? "");
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [meta, setMeta] = useState<DocMeta>({});
  const [themeId, setThemeId] = useState<string>("");
  const [layoutKey, setLayoutKey] = useState("technical");
  const [errors, setErrors] = useState<MetaErrors & { title?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  // In a client's space the Client field defaults to that client (shown once Audience is "Client"), also when the
  // template brings a stand-in name. Outside one it is the template's name, if any. A name typed by hand is kept.
  const autoClient = useRef<string | undefined>(undefined);

  const pick = (c: Choice) => {
    setChoice(c);
    setMeta(startMeta(c.template));
    autoClient.current = c.template?.meta.client;
    setThemeId(c.template?.themeId ?? themes.find((t) => t.isDefault)?.id ?? "");
    setLayoutKey(c.template?.layoutKey ?? "technical");
    setErrors({});
  };

  // Deep links: ?template=<id> or ?kind=page pick the option directly.
  useEffect(() => {
    if (choice || !templates) return;
    const t = props.template ? templates.find((x) => x.id === props.template) : undefined;
    if (t) pick({ kind: "document", template: t });
    else if (props.kind === "page") pick({ kind: "page", template: null });
  });
  useEffect(() => {
    const fallback = spaces.find((s) => s.kind === "internal") ?? spaces[0];
    if (!spaceId && fallback) setSpaceId(fallback.id);
  }, [spaceId, spaces]);
  useEffect(() => {
    if (!choice || choice.kind === "page") return;
    const s = spaces.find((x) => x.id === spaceId);
    const name = s?.kind === "client" ? s.name : choice.template?.meta.client;
    const prev = autoClient.current;
    autoClient.current = name;
    setMeta((m) => {
      if (m.client && m.client !== prev) return m;
      // Nothing to change: the same object, so that no render follows (the list of spaces is a new array on
      // every render until it has loaded, and this effect runs each time).
      if (m.client === name) return m;
      const { client: _old, ...rest } = m;
      return name ? { ...rest, client: name } : rest;
    });
  }, [spaceId, spaces, choice]);
  useEffect(() => {
    if (!titleTouched && choice?.kind !== "page") setTitle(meta.subtitle ?? "");
  }, [meta.subtitle, titleTouched, choice]);

  const layouts = useMemo(() => themes.find((t) => t.id === themeId)?.published?.layouts ?? [], [themes, themeId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!choice) return;
    const metaErrors = validateMeta(choice.kind, meta);
    const next: typeof errors = { ...metaErrors };
    if (!title.trim()) next.title = "Enter a title.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    try {
      const doc = await api.documents.create({
        spaceId,
        parentId: props.parent ?? null,
        kind: choice.kind,
        title: title.trim(),
        meta: choice.kind === "page" ? {} : cleanMeta(meta),
        ...(choice.template ? { fromTemplate: choice.template.id } : {}),
        // An empty themeId is left out: the server refuses null and picks the default theme itself.
        ...(choice.kind === "page" ? {} : { ...(themeId ? { themeId } : {}), layoutKey }),
      });
      await navigate({ to: "/d/$ref", params: { ref: doc.id }, search: { edit: true } });
    } catch (err) {
      if (err instanceof ApiError && err.code === "code_taken") setErrors({ code: err.message });
      else setErrors({ form: err instanceof Error ? err.message : "The document could not be created." });
    } finally {
      setBusy(false);
    }
  };

  type Option = { id: string; label: string; hint: string; icon: ReactElement; choice: Choice };
  const fromTemplate = (t: Template): Option => ({
    id: t.id,
    label: t.title,
    hint: t.meta.docType ?? "Template",
    icon: <LayoutTemplate size={16} className="text-green" aria-hidden="true" />,
    choice: { kind: "document", template: t },
  });
  const blank: Option[] = [
    {
      id: "doc",
      label: "Blank document",
      hint: "With cover and metadata",
      icon: <FileText size={16} className="text-green" aria-hidden="true" />,
      choice: { kind: "document", template: null },
    },
    {
      id: "page",
      label: "Blank wiki page",
      hint: "No metadata, no cover",
      icon: <NotebookText size={16} className="text-ink-3" aria-hidden="true" />,
      choice: { kind: "page", template: null },
    },
    ...(can(me, "admin")
      ? [
          {
            id: "tpl",
            label: "Blank template",
            hint: "Offered under New from template",
            icon: <LayoutTemplate size={16} className="text-orange" aria-hidden="true" />,
            choice: { kind: "template" as const, template: null },
          },
        ]
      : []),
  ];
  // The templates by who they are written for, then the blank choices. Any template can start a document in any
  // topic or client: the groups sort the list, they do not narrow it.
  const groups: Array<{ label: string; options: Option[] }> = [
    ...AUDIENCES.map((a) => ({
      label: `${a.heading} templates`,
      options: (templates ?? []).filter((t) => t.audience === a.key).map(fromTemplate),
    })),
    { label: "Blank", options: blank },
  ].filter((g) => g.options.length > 0);
  const selectedId = choice
    ? (choice.template?.id ?? (choice.kind === "page" ? "page" : choice.kind === "template" ? "tpl" : "doc"))
    : null;

  return (
    <>
      <PageBar>
        <Crumb current>New document</Crumb>
      </PageBar>
      <Page className="max-w-5xl">
        <PageTitle>Start a document</PageTitle>
        {isLoading ? <Spinner label="Loading templates…" /> : null}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink-2">Start from</legend>
            <div role="radiogroup" aria-label="Start from" className="grid gap-5">
              {groups.map((g) => (
                // biome-ignore lint/a11y/useSemanticElements: a labelled group of radios inside the radio group
                <div key={g.label} role="group" aria-label={g.label} className="grid gap-2">
                  <p aria-hidden="true" className="text-xs font-semibold text-ink-3">
                    {g.label}
                  </p>
                  {g.options.map((o) => (
                    // biome-ignore lint/a11y/useSemanticElements: card-style radio rendered as a button
                    <button
                      key={o.id}
                      type="button"
                      role="radio"
                      aria-checked={selectedId === o.id}
                      onClick={() => pick(o.choice)}
                      className={clsx(
                        "flex items-start gap-3 rounded-lg border bg-surface px-4 py-3 text-left",
                        selectedId === o.id ? "border-green ring-1 ring-green" : "border-line hover:border-line-strong",
                      )}
                    >
                      <span className="shrink-0 pt-1">{o.icon}</span>
                      <span className="grid min-w-0">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-medium text-ink">
                          {o.label}
                          {o.choice.template ? <DocMarks code={o.choice.template.code} kind="template" /> : null}
                        </span>
                        <span className="text-xs text-ink-3">{o.hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </fieldset>

          {choice ? (
            <form
              onSubmit={submit}
              noValidate
              className="grid content-start gap-4 rounded-xl border border-line bg-surface p-5"
            >
              {errors.form ? (
                <p role="alert" className="rounded-md bg-danger-wash px-3 py-2 text-sm text-danger">
                  {errors.form}
                </p>
              ) : null}
              <Field label="Topic or client">
                {(id) => (
                  <Select
                    id={id}
                    value={spaceId}
                    onChange={setSpaceId}
                    options={SPACE_GROUPS.map(([kind, label]) => ({
                      label,
                      options: spaces.filter((s) => s.kind === kind).map((s) => ({ value: s.id, label: s.name })),
                    })).filter((g) => g.options.length > 0)}
                  />
                )}
              </Field>
              <Field
                label="Title"
                error={errors.title}
                hint={
                  choice.kind === "page" ? undefined : "Shown in the tree. Follows the subtitle until you change it."
                }
              >
                {(id, describedBy) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    invalid={Boolean(errors.title)}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setTitleTouched(true);
                    }}
                  />
                )}
              </Field>
              {choice.kind !== "page" ? (
                <>
                  <MetaForm kind={choice.kind} meta={meta} onChange={setMeta} errors={errors} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Theme">
                      {(id) => (
                        <Select
                          id={id}
                          value={themeId}
                          onChange={setThemeId}
                          options={themes
                            .filter((t) => !t.archived && t.published)
                            .map((t) => ({ value: t.id, label: `${t.name}${t.isDefault ? " (default)" : ""}` }))}
                        />
                      )}
                    </Field>
                    <Field label="Layout">
                      {(id) => (
                        <Select
                          id={id}
                          value={layoutKey}
                          onChange={setLayoutKey}
                          options={(layouts.length ? layouts : [{ key: "technical", name: "Technical" }]).map((l) => ({
                            value: l.key,
                            label: l.name,
                          }))}
                        />
                      )}
                    </Field>
                  </div>
                </>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit" variant="primary" disabled={busy || !spaceId}>
                  Create and edit
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-ink-3">Choose a template or a blank document to continue.</p>
          )}
        </div>
      </Page>
    </>
  );
}
